import { toAsyncIterable } from './toAsyncIterable';

type Resolvable<R> = R | PromiseLike<R>;

type IterateFunction<T, R> = (item: T, index: number) => Resolvable<R>;

/**
 * Iterate over an array (or Iterable) which contains promises (or a mix of promises and values) with the given iterator function with the signature `(item, index)`
 * where item is the resolved value of a respective promise in the input array.
 * Iteration happens serially. If any promise in the input array is rejected the returned promise is rejected as well.
 *
 * Resolves to a new array containing the resolved values of the original input elements
 * If the iterator function returns a promise or a thenable, the result for the promise is awaited for before continuing with next iteration.
 */
export default async function each<R>(
  values: Iterable<Resolvable<R>>,
  iterator: IterateFunction<R, unknown>,
): Promise<R[]> {
  let index = 0;
  // this fn returns the resolved values of `values`
  const returnValues: R[] = [];

  for await (const val of toAsyncIterable(values)) {
    returnValues.push(val);
    await iterator(val, index++);
  }

  return returnValues;
}
